import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Check, Download, Users } from 'lucide-react';

const DEFAULT_FAQ = [
  { q: 'ต่างจาก blog หรือ content ฟรีอย่างไร?', a: 'หนังสือเรียบเรียงเป็นระบบ มี framework ครบ + อ่านจบได้ workflow ใช้งานจริง ไม่ใช่ความรู้กระจัดกระจาย' },
  { q: 'ซื้อแล้วได้อะไรบ้าง?', a: 'อ่านออนไลน์ได้ตลอด + Download PDF เก็บไว้ + เนื้อหาอัปเดตฟรีทุกครั้งที่มีเวอร์ชันใหม่' },
  { q: 'เนื้อหาอัปเดตยังไง?', a: 'เมื่อ Dr. Kim อัปเดตเนื้อหา คุณเปิดอ่านครั้งถัดไปก็เห็นเวอร์ชันใหม่ทันที ไม่ต้องซื้อใหม่' },
  { q: 'คืนเงินได้ไหม?', a: 'อ่านบทที่ 1 ฟรีก่อนตัดสินใจ — ถ้าชอบค่อยซื้อ ไม่มีความเสี่ยง' },
];
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { trackEbookEvent } from '@/lib/ebookAnalytics';
import type { AccessState, Ebook, EbookChapter, ReadingProgressRow } from '@/types/ebook';
import { EbookCard } from '@/components/ebook/EbookCard';
import { EbookLargeCover } from '@/components/ebook/EbookLargeCover';
import { EbookFAQ } from '@/components/ebook/EbookFAQ';
import { ContinueReadingBar } from '@/components/ebook/ContinueReadingBar';
import { N4PublicNav } from '@/components/n4/N4PublicNav';

export default function EbookCatalog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ebooks, setEbooks] = useState<Ebook[] | null>(null);
  const [chapterCounts, setChapterCounts] = useState<Record<string, number>>({});
  const [freeCounts, setFreeCounts] = useState<Record<string, number>>({});
  const [progress, setProgress] = useState<ReadingProgressRow | null>(null);
  const [featuredAccess, setFeaturedAccess] = useState<AccessState | null>(null);
  const [featuredCh1, setFeaturedCh1] = useState<EbookChapter | null>(null);

  useEffect(() => {
    trackEbookEvent('ebook_catalog_view', {});
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from('ebooks')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      const list = (data ?? []) as Ebook[];
      setEbooks(list);
      const ids = list.map((e) => e.id);
      if (ids.length > 0) {
        const { data: chRows } = await (supabase as any)
          .from('ebook_chapters')
          .select('ebook_id, is_free_preview')
          .in('ebook_id', ids);
        const counts: Record<string, number> = {};
        const free: Record<string, number> = {};
        ((chRows ?? []) as { ebook_id: string; is_free_preview: boolean }[]).forEach((r) => {
          counts[r.ebook_id] = (counts[r.ebook_id] ?? 0) + 1;
          if (r.is_free_preview) free[r.ebook_id] = (free[r.ebook_id] ?? 0) + 1;
        });
        setChapterCounts(counts);
        setFreeCounts(free);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) {
      setProgress(null);
      return;
    }
    (async () => {
      const { data } = await (supabase as any)
        .from('ebook_reading_progress')
        .select('ebook_id, current_chapter, progress_percent, last_read_at')
        .eq('user_id', user.id)
        .order('last_read_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      setProgress((data ?? null) as ReadingProgressRow | null);
    })();
  }, [user]);

  const [progressBook, setProgressBook] = useState<{ slug: string; title_th: string } | null>(null);
  useEffect(() => {
    if (!progress) {
      setProgressBook(null);
      return;
    }
    const fromList = ebooks?.find((e) => e.id === progress.ebook_id);
    if (fromList) {
      setProgressBook({ slug: fromList.slug, title_th: fromList.title_th });
      return;
    }
    (async () => {
      const { data } = await (supabase as any)
        .from('ebooks')
        .select('slug, title_th')
        .eq('id', progress.ebook_id)
        .maybeSingle();
      setProgressBook((data ?? null) as { slug: string; title_th: string } | null);
    })();
  }, [progress, ebooks]);


  const featured = ebooks && ebooks.length > 0 ? ebooks[0] : null;
  const otherBooks = useMemo(
    () => (ebooks && featured ? ebooks.filter((e) => e.id !== featured.id) : []),
    [ebooks, featured],
  );
  const featuredFreeCount = featured ? (freeCounts[featured.id] ?? 0) : 0;

  useEffect(() => {
    if (!featured) {
      setFeaturedAccess(null);
      setFeaturedCh1(null);
      return;
    }
    (async () => {
      const [accessRes, chRes] = await Promise.all([
        user
          ? (supabase as any).rpc('can_access_ebook', {
              p_ebook_id: featured.id,
              p_user_id: user.id,
            })
          : Promise.resolve({ data: 'login_required' }),
        (supabase as any)
          .from('ebook_chapters')
          .select('chapter_number, title_th, reading_time_minutes, is_free_preview')
          .eq('ebook_id', featured.id)
          .order('chapter_number')
          .limit(1)
          .maybeSingle(),
      ]);
      setFeaturedAccess(((accessRes.data as string) ?? 'purchase_required') as AccessState);
      setFeaturedCh1((chRes.data ?? null) as EbookChapter | null);
    })();
  }, [featured, user]);

  const featuredCta = (() => {
    if (!featured || !featuredAccess) return null;
    if (featuredAccess === 'full_access') {
      return { label: 'อ่านต่อ', onClick: () => navigate(`/ebook/${featured.slug}/read/1`) };
    }
    if (featuredAccess === 'free_preview' || featuredAccess === 'login_required') {
      return { label: 'เริ่มอ่านฟรี', onClick: () => navigate(`/ebook/${featured.slug}`) };
    }
    if (featuredAccess === 'upgrade_required') {
      return { label: 'อัปเกรด Power+', onClick: () => navigate('/pricing') };
    }
    return {
      label: `฿${featured.price_thb ?? 0} ซื้อเลย`,
      onClick: () => navigate(`/ebook/${featured.slug}`),
    };
  })();

  return (
    <>
      <Helmet>
        <title>AIM Books | AIM Academy</title>
        <meta
          name="description"
          content="หนังสือ AI สำหรับ SME ไทย — อ่านจบ 2 ชม. ใช้งานได้เลย เนื้อหาอัปเดตอัตโนมัติ"
        />
        <meta property="og:title" content="AIM Books — หนังสือ AI สำหรับธุรกิจ" />
        <meta property="og:description" content="อ่านจบ ใช้ได้เลย — เนื้อหาอัปเดตตลอด" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aimacademy.co/ebook" />
        {featured?.og_image_url && (
          <meta property="og:image" content={featured.og_image_url} />
        )}
        <link rel="canonical" href="https://aimacademy.co/ebook" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A1A26' }}>
<N4PublicNav />
        <div className="max-w-[800px] mx-auto py-8 px-4">
          {/* Hero */}
          <header className="flex items-start gap-3 mb-6">
            <BookOpen size={20} color="#05F2F2" className="mt-1 shrink-0" />
            <div>
              <h1 className="text-[20px] font-medium" style={{ color: '#F0F8FF' }}>
                AIM Books
              </h1>
              <p className="text-[12px] mt-1" style={{ color: '#6593A6' }}>
                อ่านจบ ใช้ได้เลย — เนื้อหาอัปเดตตลอด
              </p>
              {(() => {
                const totalReaders = ebooks?.reduce((s, e) => s + (e.download_count || 0), 0) ?? 0;
                if (totalReaders <= 0) return null;
                return (
                  <div className="flex items-center gap-1.5 mt-2">
                    <Users size={14} color="#05F2F2" />
                    <span className="text-[13px]" style={{ color: '#05F2F2' }}>
                      {totalReaders}+ คนอ่านแล้ว
                    </span>
                  </div>
                );
              })()}
            </div>
          </header>

          {/* Continue reading */}
          {progress && progressBook && (
            <ContinueReadingBar
              slug={progressBook.slug}
              titleTh={progressBook.title_th}
              chapter={progress.current_chapter}
              percent={progress.progress_percent}
            />
          )}

          {/* Loading */}
          {ebooks === null && (
            <div className="grid gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse"
                  style={{
                    height: 132,
                    background: 'linear-gradient(145deg, #012840 0%, #0A1A26 100%)',
                    border: '1px solid rgba(5,242,242,0.08)',
                    borderRadius: 12,
                  }}
                />
              ))}
            </div>
          )}

          {ebooks !== null && ebooks.length === 0 && (
            <div className="text-center py-16 text-[13px]" style={{ color: '#3D6D82' }}>
              ยังไม่มี ebook ในขณะนี้
            </div>
          )}

          {/* Featured book */}
          {featured && (
            <section
              className="flex flex-row flex-wrap gap-5 mb-10"
              style={{
                background: 'linear-gradient(145deg, #012840, #0A1A26)',
                border: '1px solid rgba(5,242,242,0.15)',
                borderRadius: 14,
                padding: 20,
              }}
            >
              <div className="mx-auto sm:mx-0">
                <EbookLargeCover preset={featured.cover_image_url} title={featured.title_th} />
              </div>
              <div className="flex-1 min-w-[240px]">
                {featuredFreeCount > 0 && (
                  <span
                    className="inline-block text-[10px] mb-2"
                    style={{
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'rgba(74,222,128,0.1)',
                      color: '#4ADE80',
                      border: '1px solid rgba(74,222,128,0.3)',
                    }}
                  >
                    {featuredFreeCount === 1 ? 'บทที่ 1 อ่านฟรี' : `${featuredFreeCount} บทแรกอ่านฟรี`}
                  </span>
                )}
                <h2
                  className="text-[18px] font-medium leading-snug"
                  style={{ color: '#F0F8FF' }}
                >
                  {featured.title_th}
                </h2>
                {featured.subtitle_th && (
                  <p className="text-[13px] mt-1 mb-3" style={{ color: '#6593A6' }}>
                    {featured.subtitle_th}
                  </p>
                )}
                {featured.bullets_th && featured.bullets_th.length > 0 && (
                  <ul className="grid gap-1.5 mb-4">
                    {featured.bullets_th.slice(0, 4).map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[13px]"
                        style={{ color: '#D0E4EE' }}
                      >
                        <Check
                          size={14}
                          className="mt-0.5 shrink-0"
                          style={{ color: '#05F2F2' }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {featuredCta && (
                  <button
                    type="button"
                    onClick={featuredCta.onClick}
                    className="w-full text-[14px] font-medium transition-all hover:brightness-110"
                    style={{
                      background: 'linear-gradient(135deg, #F27405, #D45E00)',
                      color: '#fff',
                      borderRadius: 10,
                      height: 44,
                      boxShadow: '0 0 20px rgba(242,116,5,0.2)',
                    }}
                  >
                    {featuredCta.label}
                  </button>
                )}
                {(() => {
                  const fcCount = chapterCounts[featured.id] ?? 0;
                  const fcRead = fcCount > 0;
                  const fcDl = !!featured.pdf_storage_path;
                  if (!fcRead && !fcDl) return null;
                  return (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {fcRead && (
                        <span
                          className="inline-flex items-center"
                          style={{
                            gap: 6,
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontSize: 10,
                            background: 'rgba(5,242,242,0.08)',
                            border: '1px solid rgba(5,242,242,0.25)',
                            color: '#05F2F2',
                          }}
                        >
                          <BookOpen size={11} />
                          อ่านบนเว็บ
                        </span>
                      )}
                      {fcDl && (
                        <span
                          className="inline-flex items-center"
                          style={{
                            gap: 6,
                            padding: '2px 8px',
                            borderRadius: 6,
                            fontSize: 10,
                            background: 'rgba(101,147,166,0.1)',
                            border: '1px solid rgba(101,147,166,0.3)',
                            color: '#6593A6',
                          }}
                        >
                          <Download size={11} />
                          ดาวน์โหลด PDF
                        </span>
                      )}
                    </div>
                  );
                })()}
                {(featuredFreeCount > 0 || featuredCh1?.is_free_preview) &&
                  featuredAccess !== 'full_access' &&
                  featuredAccess !== 'free_preview' &&
                  featuredAccess !== 'login_required' && (
                    <button
                      type="button"
                      onClick={() => navigate(`/ebook/${featured.slug}/read/1`)}
                      className="w-full inline-flex items-center justify-center gap-2 transition-all hover:brightness-110"
                      style={{
                        marginTop: 8,
                        background: 'rgba(5,242,242,0.05)',
                        border: '1px solid rgba(5,242,242,0.2)',
                        color: '#05F2F2',
                        borderRadius: 10,
                        padding: 12,
                        fontSize: 14,
                        fontWeight: 500,
                        minHeight: 44,
                      }}
                    >
                      <BookOpen size={16} />
                      อ่านบทที่ 1 ฟรี
                    </button>
                  )}
                {featured.testimonial_quote && featured.testimonial_author && (
                  <div
                    className="flex items-start gap-2 mt-3 pt-3"
                    style={{ borderTop: '1px solid rgba(101,147,166,0.08)' }}
                  >
                    <span className="shrink-0 text-[16px]" style={{ color: '#3D6D82' }}>"</span>
                    <div className="min-w-0">
                      <div className="text-[13px] italic" style={{ color: '#D0E4EE' }}>
                        {featured.testimonial_quote}
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: '#3D6D82' }}>
                        — {featured.testimonial_author}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* All books — vertical stack */}
          {otherBooks.length > 0 && (
            <section className="grid gap-3">
              <h2 className="text-[13px] font-medium" style={{ color: '#F29D52' }}>
                เล่มอื่นๆ
              </h2>
              {otherBooks.map((e) => (
                <EbookCard key={e.id} ebook={e} chapterCount={chapterCounts[e.id] ?? 0} />
              ))}
            </section>
          )}

          {/* Author Credibility */}
          <section
            className="my-8 flex items-center gap-4"
            style={{
              background: 'rgba(5,242,242,0.03)',
              borderTop: '1px solid rgba(101,147,166,0.08)',
              borderBottom: '1px solid rgba(101,147,166,0.08)',
              padding: '24px 20px',
            }}
          >
            <div
              className="flex items-center justify-center shrink-0"
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #F27405, #D45E00)',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              DK
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-medium" style={{ color: '#F0F8FF' }}>
                Dr. Kim (หมอกิม)
              </div>
              <div
                className="text-[12px] mt-1"
                style={{ color: '#6593A6', lineHeight: 1.6 }}
              >
                สอน AI ให้เจ้าของธุรกิจ SME ไทย 200+ แบรนด์
                <br />
                เน้นเอาไปใช้จริง — ไม่ใช่แค่ทฤษฎี
              </div>
            </div>
          </section>

          {/* FAQ */}
          <EbookFAQ
            items={featured?.faq_items?.length ? featured.faq_items : DEFAULT_FAQ}
            className="my-8"
          />


          {/* Footer */}
          <div
            className="text-center text-[12px] py-6"
            style={{ color: '#3D6D82' }}
          >
            เนื้อหาเดียวกับที่สอนใน workshop — อ่านกี่รอบก็ได้
          </div>
        </div>
      </div>
    </>
  );
}
